<script setup>
import {ref, reactive, computed, onMounted, nextTick, watch, onBeforeUnmount} from "vue";
import {publicFetch} from "~/composables/publicFetch.js";
import Button from "~/components/ui/Button.vue";
import Heading1 from "~/components/public/Heading1.vue";
import Card from "~/components/ui/Card.vue";
import AgendaSimpleView from "~/components/agenda/AgendaSimpleView.vue";
import MinutesViewer from "~/components/minutes/MinutesViewer.vue";
import MeetingDocuments from "~/components/meetings/MeetingDocuments.vue";
import MeetingInfo from "~/components/meetings/MeetingInfo.vue";
import ShowMoreText from "~/components/ui/ShowMoreText.vue";
import { useModal } from 'vue-final-modal';
import AdobePdfViewer from "~/components/pdf/AdobePdfViewer.vue";
import DownloadPdfModal from "~/components/modals/DownloadPdfModal.vue";

const route = useRoute();
const config = useRuntimeConfig();
const router = useRouter();
const publicSiteData = useState('publicSiteData');
const { theme } = useTheme();

const showMinutes = ref(false);
const showAgenda = ref(true);
const currentTab = ref('agenda');
const agendaItems = ref([]);
const agendaListFormat = ref('');
const current_date = ref('');
const loading = ref(false);
const hasAgenda = (d) => d?.has_agenda === true || d?.has_agenda === 1 || d?.has_agenda === '1';

// Fetch meeting data with useAsyncData for SSR compatibility
const { data: initialMeetingData, refresh } = await useAsyncData(
    `meeting-${route.params.meeting_slug}`,
    async () => {
        // Only fetch if publicSiteData is available
        if (!publicSiteData.value?.id) {
            return null;
        }

        try {
            const response = await publicFetch(`/public-sites/meetings/${route.params.meeting_slug}`);
            const raw = response.data;

            if (!raw.public_page) {
                return null;
            }

            const meetingData = {
                id: raw.id,
                title: raw.title,
                date: raw.formattedMeetingTime?.datetime || '',
                end: raw.formattedMeetingTime?.end || '',
                start: raw.start.date,
                groups: raw.groups ?? null,
                starred: raw.starred_public_page || false,
                dropdownOpen: false,
                slug: raw.slug,
                materials_published: !!raw.published_materials_status,
                materialsPublishedDate: raw.formattedMeetingTime.materials_published_at || '',
                description: raw.description,
                next_slug: raw.next_slug,
                previous_slug: raw.previous_slug,
                additional_dates: raw.additional_dates,
                time_until_start: raw.time_until_start || '',
                video_link: raw.video_link || '',
                members_and_guest_users: raw.membersAndGuestUsers || '',
                members: raw.members || '',
                guest_members: raw.guestMembers || '',
                attachments: raw.attachments || '',
                minutes: raw.minutes || '',
                minutes_attachment_id: raw.minutes_attachment_id || null,
                has_ended: raw.time_until_start === 'This meeting has ended',
                location: raw.location,
                remote_url_description: raw.remote_url_description,
                pin_code: raw.pin_code,
                remote_option: raw.remote_option,
                live_display_enabled: raw.live_display_enabled || false
            };

            // Fetch agenda
            const agendaResponse = await publicFetch(`/public-sites/meetings/${raw.slug}/agenda`);

            return {
                meeting: meetingData,
                agenda: agendaResponse.data.agenda,
                agendaListFormat: agendaResponse.data.agenda_list_format
            };
        } catch (e) {
            console.log(e);
            return null;
        }
    }
);

// Handle redirect if meeting not found
if (!initialMeetingData.value) {
    await navigateTo(`/${route.params.site_slug}/meetings`);
}

const meeting = ref(initialMeetingData.value?.meeting || {});
agendaItems.value = initialMeetingData.value?.agenda || [];
agendaListFormat.value = initialMeetingData.value?.agendaListFormat || '';

showMinutes.value = meeting.value.has_ended;
// Initial default tab is set below, once hasMinutesContent is defined.

// Refetch data after mount if publicSiteData wasn't available during SSR
onMounted(async () => {
    if (!initialMeetingData.value && publicSiteData.value?.id) {
        await nextTick(); // Wait for hydration to fully complete
        await refresh();
        // Update refs after refresh
        if (initialMeetingData.value) {
            meeting.value = initialMeetingData.value.meeting || {};
            agendaItems.value = initialMeetingData.value.agenda || [];
            agendaListFormat.value = initialMeetingData.value.agendaListFormat || '';
            showMinutes.value = meeting.value.has_ended;
            currentTab.value = (meeting.value.has_ended && hasMinutesContent.value) ? 'minutes' : 'agenda';
        }
    }
});

const switchTab = (tab_name) => {
    currentTab.value = tab_name;
};

// "Follow Live Display" button — shown when the backend reports the org's Live Display
// feature is on and the meeting has a published agenda (same rules as the admin Live
// Display button, minus the disabled state). Opens the read-only Cast Display page in a
// new window, mirroring the facilitator's "Cast Display" action.
const showFollowLiveDisplay = computed(() => !!meeting.value.live_display_enabled);

const openFollowLiveDisplay = () => {
    if (typeof window === 'undefined') return;
    const url = config.public.boardDomain + '/'
        + publicSiteData.value.organization_slug
        + '/meetings/' + meeting.value.slug + '/cast';
    window.open(
        url,
        'cast-display-' + meeting.value.id,
        'popup=yes,toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,width=1280,height=800'
    );
};

// Find agenda.pdf attachment (case-insensitive, checks title and filename)
const agendaPdfAttachment = computed(() => {
    return meeting.value.attachments?.find(a =>
        a.name?.toLowerCase() === 'agenda.pdf' ||
        a.title?.toLowerCase() === 'agenda.pdf'
    );
});

// Resolve the attachment that should render as the meeting's minutes.
// Prefers the explicit minutes_attachment_id pointer set on the meeting;
// falls back to a filename match on 'minutes.pdf' for legacy imports where
// the pointer hasn't been populated yet.
const minutesPdfAttachment = computed(() => {
    const attachments = meeting.value.attachments;
    if (!Array.isArray(attachments) || attachments.length === 0) return null;

    if (meeting.value.minutes_attachment_id) {
        const pointed = attachments.find(a => a.id === meeting.value.minutes_attachment_id);
        if (pointed) return pointed;
    }

    return attachments.find(a =>
        a.name?.toLowerCase() === 'minutes.pdf' ||
        a.title?.toLowerCase() === 'minutes.pdf'
    );
});

// Show agenda.pdf when: materials published AND no native agenda items AND agenda.pdf exists
const showAgendaPdf = computed(() => {
    const hasNativeAgenda = agendaItems.value && agendaItems.value.length > 0;
    return meeting.value.materials_published && !hasNativeAgenda && agendaPdfAttachment.value;
});

// Show the attachment-based minutes viewer when:
// - An attachment is resolvable as minutes, and
// - Either minutes_attachment_id is explicitly set (imported meetings that don't
//   use published_materials_status; wins over any native minutes), or the
//   filename fallback ('minutes.pdf') applies AND there are no native minutes AND
//   materials have been published.
const showMinutesPdf = computed(() => {
    if (!minutesPdfAttachment.value) return false;
    if (meeting.value.minutes_attachment_id) return true;
    const hasNativeMinutes = meeting.value.minutes && meeting.value.minutes.length > 0;
    return !hasNativeMinutes && meeting.value.materials_published;
});

// Any form of minutes is available to view
const hasMinutesContent = computed(() => {
    return (meeting.value.minutes && meeting.value.minutes.length > 0) || showMinutesPdf.value;
});

// Default to the Minutes tab only when minutes are actually available; otherwise
// prioritize Agenda so users don't land on an empty "not published yet" panel.
currentTab.value = (meeting.value.has_ended && hasMinutesContent.value) ? 'minutes' : 'agenda';

// Build PDF URL for viewing (raw PDF without wrapper)
const buildPdfUrl = (attachment) => {
    if (!attachment?.uuid?.short_slug) return null;
    return config.public.boardDomain + '/'
        + publicSiteData.value.organization_slug
        + '/public-site-data/meetings/'
        + route.params.meeting_slug
        + '/documents/' + attachment.uuid.short_slug + '/pdf';
};

// Build download URL (has Content-Disposition: attachment header)
const buildDownloadUrl = (attachment) => {
    if (!attachment?.uuid?.short_slug) return null;
    return config.public.boardDomain + '/'
        + publicSiteData.value.organization_slug
        + '/public-site-data/meetings/'
        + route.params.meeting_slug
        + '/documents/' + attachment.uuid.short_slug + '/download';
};

// Reactive URL for agenda PDF
const agendaPdfUrl = computed(() => {
    if (!agendaPdfAttachment.value) return null;
    return buildPdfUrl(agendaPdfAttachment.value);
});

// Reactive URL for minutes PDF
const minutesPdfUrl = computed(() => {
    if (!minutesPdfAttachment.value) return null;
    return buildPdfUrl(minutesPdfAttachment.value);
});

// Filter attachments for Supporting Documents (exclude PDFs shown in tabs)
const filteredAttachments = computed(() => {
    if (!meeting.value.attachments) return [];

    return meeting.value.attachments.filter(a => {
        const name = a.name?.toLowerCase();
        // Hide agenda.pdf if it's being shown in agenda tab
        if (name === 'agenda.pdf' && showAgendaPdf.value) return false;
        // Hide the attachment currently shown as minutes (by explicit id or filename fallback)
        if (showMinutesPdf.value && minutesPdfAttachment.value && a.id === minutesPdfAttachment.value.id) return false;
        return true;
    });
});

// ==================== Accessible PDF Support ====================

// Accessible PDF preference (shared cookie across subdomains)
const { accessibleEnabled, featureEnabled: accessibleFeatureEnabled } = useAccessiblePdfPreference();

const accessibleUrls = ref({});
const processingAccessible = ref({});

// Prefetch accessible PDFs when toggle is on
const prefetchAccessiblePdfs = () => {
    if (!accessibleEnabled.value || !meeting.value) return;
    if (showAgendaPdf.value) {
        getAccessiblePdfUrl('agenda', agendaPdfAttachment.value);
    }
    if (showMinutesPdf.value) {
        getAccessiblePdfUrl('minutes', minutesPdfAttachment.value);
    }
};

// Prefetch when feature is enabled
watch(accessibleEnabled, () => {
    if (accessibleEnabled.value) {
        prefetchAccessiblePdfs();
    }
});

// Prefetch on mount if toggle already on
onMounted(() => {
    prefetchAccessiblePdfs();
});

// Get accessible PDF URL (uses signed URL approach - no blob URLs)
const getAccessiblePdfUrl = async (cacheKey, attachment) => {
    if (!attachment || !accessibleEnabled.value) return null;

    // Return cached accessible URL if available
    if (accessibleUrls.value[cacheKey]) {
        return accessibleUrls.value[cacheKey];
    }

    // Get signed URL if not already processing
    if (!processingAccessible.value[cacheKey]) {
        processingAccessible.value[cacheKey] = true;

        try {
            // Request signed URL instead of blob
            const response = await $fetch('/public-api/pdf/accessible', {
                method: 'POST',
                body: {
                    pdfUrl: buildPdfUrl(attachment),
                    title: attachment.name || 'Document',
                    lang: 'en-US',
                    format: 'url',
                    siteSlug: route.params.site_slug,
                    pageSlug: route.params.meeting_slug,
                    pageType: 'meeting'
                }
            });

            accessibleUrls.value[cacheKey] = response.url;
            return response.url;
        } catch (err) {
            console.error('Error getting accessible PDF URL:', err);
            return null;
        } finally {
            processingAccessible.value[cacheKey] = false;
        }
    }

    return null;
};

// Append PDF Open Parameters to hide the native browser PDF toolbar and
// fit the page width to the viewer on load (fixes mobile zoom-in issue).
// Works in Chromium (PDFium) and Firefox (PDF.js); Safari ignores both.
// Safe with signed S3 URLs because fragments are never sent to the server.
const withPdfViewerParams = (url) => {
    if (!url) return url;
    return url + (url.includes('#') ? '&' : '#') + 'toolbar=0&view=FitH';
};

// Raw URL (no PDF viewer params) — used by AdobePdfViewer.
const getRawIframeSrc = (cacheKey, attachment) => {
    if (!attachment) return '';
    if (accessibleEnabled.value && accessibleUrls.value[cacheKey]) {
        return accessibleUrls.value[cacheKey];
    }
    return buildPdfUrl(attachment);
};

// Get iframe src for agenda/minutes PDF (native iframe path)
const getIframeSrc = (cacheKey, attachment) => {
    if (!attachment) return '';
    return withPdfViewerParams(getRawIframeSrc(cacheKey, attachment));
};

// Which viewer to render (Adobe PDF Embed or native browser iframe)
const { adobeEnabled } = usePdfViewer();

// Crash guard: per-tab flag, set when AdobePdfViewer emits 'failed'. Template
// falls back to the native iframe for that tab. Reset when the underlying
// attachment changes (e.g. switching to a different meeting).
const adobeFailed = reactive({ agenda: false, minutes: false });
watch(agendaPdfAttachment, () => { adobeFailed.agenda = false; });
watch(minutesPdfAttachment, () => { adobeFailed.minutes = false; });

// Open download modal for a specific PDF
const openDownloadModal = (cacheKey, attachment) => {
    if (!attachment) return;

    const attrs = reactive({
        showAccessibleOption: accessibleFeatureEnabled.value,
        loading: false,
        onClose() {
            if (attrs.loading) return;
            close();
        },
        async onDownload(type) {
            attrs.loading = true;
            try {
                if (type === 'accessible') {
                    try {
                        if (!accessibleUrls.value[cacheKey]) {
                            await getAccessiblePdfUrl(cacheKey, attachment);
                        }
                        if (accessibleUrls.value[cacheKey]) {
                            const link = document.createElement('a');
                            link.href = accessibleUrls.value[cacheKey] + '?download';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }
                    } catch (err) {
                        console.error('Accessible PDF download failed, falling back to original:', err);
                        const downloadUrl = buildDownloadUrl(attachment);
                        if (downloadUrl) {
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }
                    }
                } else {
                    const downloadUrl = buildDownloadUrl(attachment);
                    if (downloadUrl) {
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                }
            } finally {
                attrs.loading = false;
                close();
            }
        }
    });

    const { open, close } = useModal({
        component: DownloadPdfModal,
        attrs
    });
    open();
};

// ==================== End Accessible PDF Support ====================

// Download agenda/minutes PDF using native fetch so we can send the SiteId header
// and receive binary PDF bytes (publicFetch uses $fetch which defaults to JSON parsing)
const downloadingAgenda = ref(false);
const downloadingMinutes = ref(false);

const triggerPdfDownload = async (path, filename, loadingRef) => {
    if (loadingRef.value) return;
    loadingRef.value = true;
    try {
        const response = await fetch(config.public.apiUrl + path, {
            headers: {
                'Accept': 'application/pdf',
                'SiteId': publicSiteData.value.id
            }
        });
        if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (e) {
        console.error(`PDF download failed (${filename}):`, e);
    } finally {
        loadingRef.value = false;
    }
};

// Build a Nuxt-internal proxy URL that the accessible PDF pipeline can use to
// fetch the PDF from the Laravel API (which requires the SiteId header).
const buildProxyPdfUrl = (apiPath) => {
    const appUrl = config.public.appUrl || '';
    return `${appUrl}/public-api/pdf/proxy?path=${encodeURIComponent(apiPath)}&siteId=${encodeURIComponent(publicSiteData.value.id)}`;
};

// Get an accessible signed URL for a native agenda/minutes PDF via the proxy
const nativeAccessibleUrls = ref({});
const processingNativeAccessible = ref({});

const getNativeAccessiblePdfUrl = async (cacheKey, apiPath, title) => {
    if (nativeAccessibleUrls.value[cacheKey]) {
        return nativeAccessibleUrls.value[cacheKey];
    }
    if (processingNativeAccessible.value[cacheKey]) return null;

    processingNativeAccessible.value[cacheKey] = true;
    try {
        const proxyUrl = buildProxyPdfUrl(apiPath);
        const response = await $fetch('/public-api/pdf/accessible', {
            method: 'POST',
            body: {
                pdfUrl: proxyUrl,
                title,
                lang: 'en-US',
                format: 'url',
                siteSlug: route.params.site_slug,
                pageSlug: route.params.meeting_slug,
                pageType: 'meeting'
            }
        });
        nativeAccessibleUrls.value[cacheKey] = response.url;
        return response.url;
    } catch (e) {
        console.error('Accessible PDF URL generation failed:', e);
        return null;
    } finally {
        processingNativeAccessible.value[cacheKey] = false;
    }
};

// Open the DownloadPdfModal for native agenda/minutes (uses Laravel download endpoint)
const openNativeDownloadModal = (type) => {
    const isAgenda = type === 'agenda';
    const apiPath = `/public-sites/meetings/${route.params.meeting_slug}/${type}/download`;
    const filename = `${type}.pdf`;
    const title = isAgenda ? (meeting.value.title + ' Agenda') : (meeting.value.title + ' Minutes');

    const attrs = reactive({
        showAccessibleOption: accessibleFeatureEnabled.value,
        loading: false,
        onClose() {
            if (attrs.loading) return;
            close();
        },
        async onDownload(downloadType) {
            attrs.loading = true;
            try {
                if (downloadType === 'accessible') {
                    try {
                        const signedUrl = await getNativeAccessiblePdfUrl(type, apiPath, title);
                        if (signedUrl) {
                            const link = document.createElement('a');
                            link.href = signedUrl + '?download';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } else {
                            // Fallback to original if accessible generation failed
                            await triggerPdfDownload(apiPath, filename, isAgenda ? downloadingAgenda : downloadingMinutes);
                        }
                    } catch (e) {
                        console.error('Accessible download failed, falling back to original:', e);
                        await triggerPdfDownload(apiPath, filename, isAgenda ? downloadingAgenda : downloadingMinutes);
                    }
                } else {
                    await triggerPdfDownload(apiPath, filename, isAgenda ? downloadingAgenda : downloadingMinutes);
                }
            } finally {
                attrs.loading = false;
                close();
            }
        }
    });

    const { open, close } = useModal({
        component: DownloadPdfModal,
        attrs
    });
    open();
};

const downloadAgendaPdf = () => openNativeDownloadModal('agenda');
const downloadMinutesPdf = () => openNativeDownloadModal('minutes');

const fetchAgenda = async (meetingSlug, date = null) => {
    let url = '/public-sites/meetings/' + meetingSlug + '/agenda';
    if(date){
        url += '?date=' + date
    }
    loading.value = true;
    const response = await publicFetch(url);
    agendaItems.value = response.data.agenda;
    agendaListFormat.value = response.data.agenda_list_format;
    current_date.value = date ?? '';
    loading.value = false;
};
</script>

<template>
    <Title>{{ publicSiteData?.organization_display_name || '' }}: {{ meeting?.title || 'Meeting' }}</Title>

    <!-- Breadcrumbs -->
    <div class="text-sm text-gray-700 mb-10" v-if="meeting.title">
        <NuxtLink :to="`/${route.params.site_slug}/meetings`" class="text-primary">Meetings</NuxtLink>
        <span class="mx-2">/</span>
        <span class="font-medium">{{meeting.title}}</span>
    </div>

    <!-- Title and nav -->
    <div class="flex flex-col md:flex-row justify-between items-start" v-if="meeting.title">
        <div>
            <div class="flex flex-wrap items-center gap-3 mb-2">
                <Heading1 class="mb-0">{{meeting.title}}</Heading1>
                <Button
                    v-if="showFollowLiveDisplay"
                    type="primary-outline"
                    size="xs"
                    button-type="button"
                    :icon="['far', 'display']"
                    @click="openFollowLiveDisplay"
                >
                    Follow Live Display
                </Button>
            </div>
            <div class="text-gray-700 font-light text-sm flex gap-1 items-center mb-2" v-if="meeting.date">
                <font-awesome class="w-3 h-3"  :icon="['far', 'calendar']" />
                {{ meeting.date }}
            </div>
        </div>
        <div>
            <div class="flex gap-4 mt-4 md:mt-0">
                <NuxtLink v-if="meeting.previous_slug" :to="`/${route.params.site_slug}/meetings/${meeting.previous_slug}`" class="p-2 flex items-center justify-center gap-1 border-gray-400 hover:bg-gray-200 text-black bg-white border rounded-md px-2 py-1 text-sm min-h-[32px] min-w-[42px]">
                    <font-awesome class="h-3.5 w-3.5" :icon="['far', 'chevron-left']" />
                    Prev Meeting
                </NuxtLink>
                <NuxtLink v-if="meeting.next_slug" :to="`/${route.params.site_slug}/meetings/${meeting.next_slug}`" class="p-2 flex items-center justify-center gap-1 border-gray-400 hover:bg-gray-200 text-black bg-white border rounded-md px-2 py-1 text-sm min-h-[32px] min-w-[42px]">
                    Next Meeting
                    <font-awesome class="h-3.5 w-3.5 ml-1" :icon="['far', 'chevron-right']" />
                </NuxtLink>
            </div>
        </div>
    </div>
    <div class="flex items-start gap-3 mb-6" v-if="meeting.description">
        <div class="flex-1">
            <ShowMoreText :content="meeting.description" :max-height="96"  />
        </div>
    </div>

    <!-- Additional dates -->
    <ul class="flex nav nav-tabs mb-4 text-gray-700" v-if="meeting.additional_dates && meeting.additional_dates.length">
        <li class="nav-item">
            <a @click="fetchAgenda(meeting.slug)" class="nav-link cursor-pointer mr-4" :class="{'text-primary border-b border-primary pb-2' : !current_date}">Day 1</a>
        </li>
        <template v-for="(date, i) in meeting.additional_dates" :key="date.id">
            <li v-if="hasAgenda(date)" class="nav-item">
                <a @click="fetchAgenda(meeting.slug, date.id)" class="nav-link cursor-pointer mr-4" :class="{'text-primary border-b border-primary pb-2' : current_date && current_date === date.id}">Day {{ i + 2 }}</a>
            </li>
        </template>
    </ul>

    <div class="flex-col gap-8 items-start md:flex md:flex-row space-y-6 md:space-y-0">
        <div class="w-full md:w-2/3">
            <!-- Agenda and minutes -->
            <Card>
                <template #header>
                    <div class="flex items-center justify-between w-full">
                        <div class="flex relative mx-2 top-px">
                            <a href="#"
                               v-if="showMinutes"
                               @click.prevent="switchTab('minutes')"
                               class="font-medium p-4 block border-b-2"
                               :class="currentTab === 'minutes' ? '' : 'text-gray-900'"
                               :style="{'borderColor': currentTab === 'minutes' ? theme.medium : 'transparent'}"
                            >Minutes</a>
                            <a href="#"
                               v-if="showAgenda"
                               @click.prevent="switchTab('agenda')"
                               class="font-medium p-4 block border-b-2"
                               :class="currentTab === 'agenda' ? '' : 'text-gray-900'"
                               :style="{'borderColor': currentTab === 'agenda' ? theme.medium : 'transparent'}"
                            >Agenda</a>
                        </div>

                        <div class="pe-4 flex items-center gap-4">
                            <button
                                v-if="showAgendaPdf && currentTab === 'agenda'"
                                type="button"
                                class="p-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                                aria-haspopup="dialog"
                                :disabled="processingAccessible['agenda']"
                                @click="openDownloadModal('agenda', agendaPdfAttachment)"
                            >
                                <span>Download</span>
                                <font-awesome :icon="['far', 'download']" class="w-5 h-5" aria-hidden="true" />
                            </button>

                            <button
                                v-if="showMinutesPdf && currentTab === 'minutes'"
                                type="button"
                                class="p-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                                aria-haspopup="dialog"
                                :disabled="processingAccessible['minutes']"
                                @click="openDownloadModal('minutes', minutesPdfAttachment)"
                            >
                                <span>Download</span>
                                <font-awesome :icon="['far', 'download']" class="w-5 h-5" aria-hidden="true" />
                            </button>

                            <!-- Download agenda PDF button (native agenda) -->
                            <button
                                v-if="agendaItems.length && meeting.materials_published && currentTab === 'agenda'"
                                type="button"
                                :disabled="downloadingAgenda"
                                class="p-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2 disabled:opacity-50"
                                aria-haspopup="dialog"
                                @click="downloadAgendaPdf"
                            >
                                <span>Download</span>
                                <font-awesome v-if="downloadingAgenda" :icon="['fas', 'spinner']" class="w-5 h-5 animate-spin" aria-hidden="true" />
                                <font-awesome v-else :icon="['far', 'download']" class="w-5 h-5" aria-hidden="true" />
                            </button>

                            <!-- Download minutes PDF button (native minutes) — only when the attachment PDF is not taking over -->
                            <button
                                v-if="showMinutes && meeting.minutes?.length && !showMinutesPdf && currentTab === 'minutes'"
                                type="button"
                                :disabled="downloadingMinutes"
                                class="p-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2 disabled:opacity-50"
                                aria-haspopup="dialog"
                                @click="downloadMinutesPdf"
                            >
                                <span>Download</span>
                                <font-awesome v-if="downloadingMinutes" :icon="['fas', 'spinner']" class="w-5 h-5 animate-spin" aria-hidden="true" />
                                <font-awesome v-else :icon="['far', 'download']" class="w-5 h-5" aria-hidden="true" />
                            </button>

                            <!-- Full Agenda button (only for native agenda) -->
                            <div v-if="agendaItems.length && meeting.materials_published && currentTab === 'agenda'">
                                <NuxtLink :to="`/${route.params.site_slug}/meetings/${meeting.slug}/agenda?date=${current_date}`" class="p-2 flex items-center justify-center gap-1 border-gray-400 hover:bg-gray-200 text-black bg-white border rounded-md px-2 py-1 text-sm min-h-[32px] min-w-[42px]">
                                    Full agenda&nbsp;
                                    <font-awesome class="h-3.5 w-3.5 ml-1" :icon="['fas', 'up-right-and-down-left-from-center']" />
                                </NuxtLink>
                            </div>
                        </div>
                    </div>
                </template>
                <div v-show="currentTab==='agenda'">
                    <!-- Loading state -->
                    <div v-if="loading" class="flex items-center justify-center h-32 w-full">
                        <font-awesome :icon="['fas', 'spinner']" class="animate-spin text-primary h-8 w-8" />
                    </div>

                    <!-- Native Boardable agenda -->
                    <AgendaSimpleView
                        v-else-if="agendaItems.length && meeting.materials_published"
                        :meeting="meeting"
                        :agenda-items="agendaItems"
                        :agenda-list-format="agendaListFormat"
                        ref="simple_agenda_view"
                        :current_date="current_date"
                    ></AgendaSimpleView>

                    <!-- Fallback: agenda.pdf display -->
                    <div v-else-if="showAgendaPdf" class="p-0">
                        <AdobePdfViewer
                            v-if="currentTab === 'agenda' && adobeEnabled && !adobeFailed.agenda"
                            :key="`agenda-adobe-${accessibleEnabled}`"
                            :src="getRawIframeSrc('agenda', agendaPdfAttachment)"
                            :pdf-title="agendaPdfAttachment?.title || agendaPdfAttachment?.name || 'Agenda'"
                            container-class="w-full h-[80vh] rounded-lg"
                            :show-download="false"
                            @failed="adobeFailed.agenda = true"
                        />
                        <iframe
                            v-else-if="currentTab === 'agenda'"
                            :key="`agenda-iframe-${accessibleEnabled}`"
                            :src="getIframeSrc('agenda', agendaPdfAttachment)"
                            title="Agenda PDF"
                            class="w-full h-[80vh] rounded-lg"
                        />
                    </div>

                    <!-- Materials not published -->
                    <div v-else-if="!meeting.materials_published" class="py-6 text-gray-700 text-center font-light">
                        Materials not published yet.
                    </div>

                    <!-- No agenda found -->
                    <div v-else class="py-6 text-gray-700 text-center font-light">
                        Agenda not found
                    </div>
                </div>
                <template v-if="showMinutes">
                    <div v-show="currentTab==='minutes'">
                        <!-- Attachment PDF (explicit minutes_attachment_id or filename fallback) wins over native minutes -->
                        <div v-if="showMinutesPdf" class="p-0">
                            <AdobePdfViewer
                                v-if="currentTab === 'minutes' && adobeEnabled && !adobeFailed.minutes"
                                :key="`minutes-adobe-${accessibleEnabled}`"
                                :src="getRawIframeSrc('minutes', minutesPdfAttachment)"
                                :pdf-title="minutesPdfAttachment?.title || minutesPdfAttachment?.name || 'Minutes'"
                                container-class="w-full h-[80vh] rounded-lg"
                                :show-download="false"
                                @failed="adobeFailed.minutes = true"
                            />
                            <iframe
                                v-else-if="currentTab === 'minutes'"
                                :key="`minutes-iframe-${accessibleEnabled}`"
                                :src="getIframeSrc('minutes', minutesPdfAttachment)"
                                title="Minutes PDF"
                                class="w-full h-[80vh] rounded-lg"
                            />
                        </div>

                        <!-- Native Boardable minutes (used when no attachment PDF applies) -->
                        <MinutesViewer
                            v-else-if="meeting.minutes && meeting.minutes.length"
                            :meeting="meeting"
                            ref="minutes_viewer"
                            class="h-full"
                        ></MinutesViewer>

                        <!-- Minutes not published -->
                        <div v-else class="py-6 text-gray-700 text-center font-light">
                            Minutes for this meeting have not been published yet.
                        </div>
                    </div>
                </template>
            </Card>


            <Card v-if="false">
                <template #header>
                    <div class="flex items-center justify-between w-full p-4">
                        <div v-if="meeting.has_ended" class="flex">
                            <div  class="flex flex-col relative mr-6">
                                <h3 class="text-lg font-medium text-gray-900 cursor-pointer" @click.prevent="handleCardSwitch()">Minutes</h3>
                            </div>
                            <div  class="flex flex-col relative">
                                <h3 class="text-lg font-medium text-primary cursor-pointer">Agenda</h3>
                                <div class="border-b border-b-primary w-full absolute" style="bottom:-19px;"></div>
                            </div>
                        </div>
                        <h3 v-else class="text-lg font-medium text-gray-900">Agenda</h3>

                    </div>
                </template>
            </Card>
        </div>

        <div class="w-full md:w-1/3 space-y-6">
            <MeetingInfo :meeting="meeting"></MeetingInfo>
            <MeetingDocuments
                v-if="filteredAttachments.length && meeting.materials_published"
                :meeting="meeting"
                :attachments="filteredAttachments"
            ></MeetingDocuments>
        </div>
    </div>


</template>
